/**
 * Partikelsystem + Projektile (BATTLE-14). Pool-basiert, Budget-gedeckelt —
 * "kraeftig, aber niemals unlesbar" (docs/11 VFX Style).
 * Farben kommen von den Aufrufern aus KEYWORD_COLORS (docs/12 §2).
 */
import { lighten, rgba } from './draw3d'

export type ParticleKind =
  | 'dot' | 'spark' | 'bubble' | 'star' | 'puff' | 'ring'
  // Ability-Cast-Kinds (BATTLE-14 VFX): klare Formen, kurz, lesbar bei 10v10
  | 'dome' | 'streak' | 'flash' | 'groundRing' | 'groundGlow' | 'implode' | 'pulse'

interface Particle {
  kind: ParticleKind
  x: number
  y: number
  vx: number
  vy: number
  size: number
  color: string
  bornMs: number
  ttlMs: number
  gravity: number
  drag: number
}

interface Projectile {
  x1: number
  y1: number
  x2: number
  y2: number
  color: string
  bornMs: number
  ttlMs: number
  size: number
}

const MAX_PARTICLES = 420

/** deterministischer kleiner Zufall fuers Streuen (FX-only, kein Sim-Bezug) */
function rnd(): number {
  return Math.random()
}

export class ParticleSystem {
  private particles: Particle[] = []
  private projectiles: Projectile[] = []

  reset(): void {
    this.particles = []
    this.projectiles = []
  }

  get count(): number {
    return this.particles.length
  }

  private push(p: Particle): void {
    if (this.particles.length >= MAX_PARTICLES) this.particles.shift()
    this.particles.push(p)
  }

  /** radiale Funken (Treffer) */
  sparks(x: number, y: number, color: string, nowMs: number, count = 6, speed = 90): void {
    for (let i = 0; i < count; i++) {
      const a = rnd() * Math.PI * 2
      const v = speed * (0.5 + rnd() * 0.8)
      this.push({
        kind: 'spark', x, y,
        vx: Math.cos(a) * v, vy: Math.sin(a) * v - 30,
        size: 1.5 + rnd() * 2, color, bornMs: nowMs, ttlMs: 320 + rnd() * 180,
        gravity: 240, drag: 0.9,
      })
    }
  }

  /** aufsteigende Gift-Blasen */
  bubbles(x: number, y: number, color: string, nowMs: number, count = 4): void {
    for (let i = 0; i < count; i++) {
      this.push({
        kind: 'bubble', x: x + (rnd() - 0.5) * 18, y: y + (rnd() - 0.5) * 8,
        vx: (rnd() - 0.5) * 12, vy: -28 - rnd() * 22,
        size: 2.5 + rnd() * 3, color, bornMs: nowMs, ttlMs: 650 + rnd() * 300,
        gravity: -12, drag: 0.98,
      })
    }
  }

  /** Glut-Flocken (Brand) */
  embers(x: number, y: number, color: string, nowMs: number, count = 5): void {
    for (let i = 0; i < count; i++) {
      this.push({
        kind: 'spark', x: x + (rnd() - 0.5) * 16, y: y + (rnd() - 0.5) * 10,
        vx: (rnd() - 0.5) * 26, vy: -40 - rnd() * 30,
        size: 1.5 + rnd() * 2.2, color, bornMs: nowMs, ttlMs: 500 + rnd() * 250,
        gravity: -20, drag: 0.96,
      })
    }
  }

  /** Heil-Funkeln (Sterne steigen auf) */
  sparkles(x: number, y: number, color: string, nowMs: number, count = 7): void {
    for (let i = 0; i < count; i++) {
      this.push({
        kind: 'star', x: x + (rnd() - 0.5) * 26, y: y + (rnd() - 0.5) * 16,
        vx: (rnd() - 0.5) * 10, vy: -35 - rnd() * 25,
        size: 2 + rnd() * 2.5, color, bornMs: nowMs, ttlMs: 700 + rnd() * 300,
        gravity: -8, drag: 0.985,
      })
    }
  }

  /** Schild-Schimmer (Bogen-Punkte um die Unit) */
  shimmer(x: number, y: number, r: number, color: string, nowMs: number, count = 8): void {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2
      this.push({
        kind: 'dot', x: x + Math.cos(a) * r, y: y + Math.sin(a) * r * 0.8,
        vx: Math.cos(a) * 14, vy: Math.sin(a) * 10 - 6,
        size: 2 + rnd(), color, bornMs: nowMs, ttlMs: 500 + rnd() * 200,
        gravity: 0, drag: 0.95,
      })
    }
  }

  /** Todes-Poof (graue Wolke) */
  poof(x: number, y: number, nowMs: number, count = 8): void {
    for (let i = 0; i < count; i++) {
      const a = rnd() * Math.PI * 2
      this.push({
        kind: 'puff', x, y: y + 4,
        vx: Math.cos(a) * 28 * rnd(), vy: -12 - rnd() * 26,
        size: 4 + rnd() * 5, color: '#8d867a', bornMs: nowMs, ttlMs: 550 + rnd() * 250,
        gravity: -10, drag: 0.94,
      })
    }
  }

  /** expandierender Ring (Casts/Impacts) */
  ring(x: number, y: number, color: string, nowMs: number, size = 26): void {
    this.push({
      kind: 'ring', x, y, vx: 0, vy: 0,
      size, color, bornMs: nowMs, ttlMs: 420, gravity: 0, drag: 1,
    })
  }

  // ======== Ability-Cast-Spawns (BATTLE-14) — Farben kommen aus theme.ts ========

  /** Bollwerk: expandierende Schild-Halbkugel (Boegen + Basis-Ellipse) ueber der Unit */
  shieldDome(x: number, baseY: number, maxR: number, color: string, nowMs: number, ttlMs = 650): void {
    this.push({
      kind: 'dome', x, y: baseY, vx: 0, vy: 0,
      size: maxR, color, bornMs: nowMs, ttlMs, gravity: 0, drag: 1,
    })
  }

  /** Schattenstoss: Schlieren die von aussen ins Zentrum gezogen werden */
  streaksIn(x: number, y: number, r: number, color: string, nowMs: number, count = 10): void {
    const ttl = 400
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + rnd() * 0.5
      const d = r * (1.4 + rnd() * 0.55)
      const sx = x + Math.cos(a) * d
      const sy = y + Math.sin(a) * d * 0.72
      const life = ttl * (0.75 + rnd() * 0.35)
      // Geschwindigkeit so, dass die Schliere ~am Lebensende das Zentrum erreicht
      const v = 1000 / life
      this.push({
        kind: 'streak', x: sx, y: sy,
        vx: (x - sx) * v, vy: (y - sy) * v,
        size: 3 + rnd() * 2, color, bornMs: nowMs, ttlMs: life, gravity: 0, drag: 1,
      })
    }
  }

  /** kurzer dunkler/heller Soft-Puls (gefuellter Radial-Kreis) */
  pulse(x: number, y: number, color: string, nowMs: number, size: number, ttlMs = 280): void {
    this.push({
      kind: 'pulse', x, y, vx: 0, vy: 0,
      size, color, bornMs: nowMs, ttlMs, gravity: 0, drag: 1,
    })
  }

  /** kontrahierender Ring (Implosion, Schatten-Casts) */
  implosion(x: number, y: number, color: string, nowMs: number, size: number): void {
    this.push({
      kind: 'implode', x, y, vx: 0, vy: 0,
      size, color, bornMs: nowMs, ttlMs: 320, gravity: 0, drag: 1,
    })
  }

  /** Muendungsblitz: Stern-Burst, sehr kurz (Durchschlag) */
  muzzleFlash(x: number, y: number, color: string, nowMs: number, size: number): void {
    this.push({
      kind: 'flash', x, y, vx: 0, vy: 0,
      size, color, bornMs: nowMs, ttlMs: 200, gravity: 0, drag: 1,
    })
  }

  /** horizontale Speed-Lines, beidseitig nach aussen schiessend (Durchschlag) */
  speedLines(x: number, y: number, color: string, nowMs: number, count = 10, speed = 460): void {
    for (let i = 0; i < count; i++) {
      const side = i % 2 === 0 ? 1 : -1
      const v = speed * (0.7 + rnd() * 0.6)
      this.push({
        kind: 'streak', x: x + side * (6 + rnd() * 10), y: y + (rnd() - 0.5) * 20,
        vx: side * v, vy: 0,
        size: 2.4 + rnd() * 1.4, color, bornMs: nowMs, ttlMs: 280 + rnd() * 150, gravity: 0, drag: 0.97,
      })
    }
  }

  /** Heilwelle: Funkenregen — Sterne steigen ueber der ganzen Unit-Breite auf */
  starRain(x: number, y: number, w: number, color: string, nowMs: number, count = 14): void {
    for (let i = 0; i < count; i++) {
      this.push({
        kind: 'star', x: x + (rnd() - 0.5) * w, y: y + (rnd() - 0.5) * w * 0.45,
        vx: (rnd() - 0.5) * 8, vy: -42 - rnd() * 34,
        size: 2.4 + rnd() * 2.6, color, bornMs: nowMs, ttlMs: 650 + rnd() * 380,
        gravity: -10, drag: 0.985,
      })
    }
  }

  /** expandierender Boden-Ring (perspektivische Ellipse — Heilkreis, Giftring) */
  groundRing(x: number, y: number, color: string, nowMs: number, size: number, ttlMs = 650): void {
    this.push({
      kind: 'groundRing', x, y, vx: 0, vy: 0,
      size, color, bornMs: nowMs, ttlMs, gravity: 0, drag: 1,
    })
  }

  /** weicher Boden-Glow (gefuellte Boden-Ellipse, Heilkreis) */
  groundGlow(x: number, y: number, color: string, nowMs: number, size: number, ttlMs = 800): void {
    this.push({
      kind: 'groundGlow', x, y, vx: 0, vy: 0,
      size, color, bornMs: nowMs, ttlMs, gravity: 0, drag: 1,
    })
  }

  /** traege expandierende farbige Wolke (Seuchenwolke) — umhuellt die Unit */
  cloud(x: number, y: number, color: string, nowMs: number, count = 9, radius = 26): void {
    for (let i = 0; i < count; i++) {
      const a = rnd() * Math.PI * 2
      const d = radius * (0.2 + rnd() * 0.65)
      this.push({
        kind: 'puff', x: x + Math.cos(a) * d, y: y + Math.sin(a) * d * 0.75,
        vx: Math.cos(a) * (11 + rnd() * 15), vy: Math.sin(a) * (7 + rnd() * 9) - 3,
        size: 4.5 + rnd() * 5, color, bornMs: nowMs, ttlMs: 850 + rnd() * 350,
        gravity: -4, drag: 0.965,
      })
    }
  }

  /** fliegendes Projektil (Ranged-Attacks) */
  projectile(x1: number, y1: number, x2: number, y2: number, color: string, nowMs: number, durMs = 130): void {
    if (this.projectiles.length > 40) this.projectiles.shift()
    this.projectiles.push({ x1, y1, x2, y2, color, bornMs: nowMs, ttlMs: durMs, size: 3 })
  }

  /** Physik-Update + Zeichnen in einem Pass (dt aus echter Frame-Zeit) */
  updateAndDraw(ctx: CanvasRenderingContext2D, nowMs: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000)
    // Projektile (mit kurzem Trail)
    this.projectiles = this.projectiles.filter((p) => nowMs - p.bornMs <= p.ttlMs)
    for (const p of this.projectiles) {
      const t = Math.max(0, Math.min(1, (nowMs - p.bornMs) / p.ttlMs))
      const x = p.x1 + (p.x2 - p.x1) * t
      const y = p.y1 + (p.y2 - p.y1) * t
      const tx = p.x1 + (p.x2 - p.x1) * Math.max(0, t - 0.18)
      const ty = p.y1 + (p.y2 - p.y1) * Math.max(0, t - 0.18)
      const grad = ctx.createLinearGradient(tx, ty, x, y)
      grad.addColorStop(0, rgba(p.color, 0))
      grad.addColorStop(1, rgba(p.color, 0.9))
      ctx.strokeStyle = grad
      ctx.lineWidth = p.size
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(tx, ty)
      ctx.lineTo(x, y)
      ctx.stroke()
      ctx.fillStyle = p.color
      ctx.beginPath()
      ctx.arc(x, y, p.size * 0.8, 0, Math.PI * 2)
      ctx.fill()
    }
    // Partikel
    this.particles = this.particles.filter((p) => nowMs - p.bornMs <= p.ttlMs)
    for (const p of this.particles) {
      p.vx *= p.drag
      p.vy = p.vy * p.drag + p.gravity * dt
      p.x += p.vx * dt
      p.y += p.vy * dt
      const life = 1 - (nowMs - p.bornMs) / p.ttlMs
      ctx.globalAlpha = Math.max(0, life)
      switch (p.kind) {
        case 'ring': {
          const t = 1 - life
          ctx.strokeStyle = p.color
          ctx.lineWidth = 2.5 * life
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size * (0.4 + t * 1.1), 0, Math.PI * 2)
          ctx.stroke()
          break
        }
        case 'star': {
          ctx.fillStyle = p.color
          const s = p.size
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.rotate((nowMs - p.bornMs) / 200)
          ctx.beginPath()
          for (let i = 0; i < 4; i++) {
            ctx.moveTo(0, 0)
            const a = (i / 4) * Math.PI * 2
            ctx.lineTo(Math.cos(a) * s * 1.8, Math.sin(a) * s * 1.8)
          }
          ctx.lineWidth = 1.4
          ctx.strokeStyle = p.color
          ctx.stroke()
          ctx.restore()
          break
        }
        case 'puff': {
          const t = 1 - life
          ctx.fillStyle = p.color
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size * (0.6 + t * 0.9), 0, Math.PI * 2)
          ctx.fill()
          break
        }
        case 'bubble': {
          ctx.strokeStyle = p.color
          ctx.lineWidth = 1.3
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
          ctx.stroke()
          break
        }
        case 'dome': {
          // Schild-Halbkugel: Basis-Ellipse + Kuppel-Silhouette + Meridian/Breitengrad
          const t = 1 - life
          const ease = 1 - (1 - t) ** 3
          const R = p.size * (0.5 + 0.5 * ease)
          ctx.globalAlpha = Math.min(1, life * 1.7)
          // Kuppel-Fuellung (leichter Glow, aussen dichter)
          const g = ctx.createRadialGradient(p.x, p.y - R * 0.25, R * 0.15, p.x, p.y, R)
          g.addColorStop(0, rgba(p.color, 0.07))
          g.addColorStop(1, rgba(p.color, 0.32))
          ctx.fillStyle = g
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, R, R * 0.92, 0, Math.PI, Math.PI * 2)
          ctx.ellipse(p.x, p.y, R, R * 0.3, 0, 0, Math.PI)
          ctx.closePath()
          ctx.fill()
          // Kuppel-Silhouette
          ctx.strokeStyle = p.color
          ctx.lineWidth = 3.2
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, R, R * 0.92, 0, Math.PI, Math.PI * 2)
          ctx.stroke()
          // Basis-Ellipse am Boden
          ctx.lineWidth = 2.2
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, R, R * 0.3, 0, 0, Math.PI * 2)
          ctx.stroke()
          // Meridian + Breitengrad (Halbkugel-Boegen)
          ctx.globalAlpha = Math.min(1, life * 1.7) * 0.55
          ctx.lineWidth = 1.4
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, R * 0.45, R * 0.92, 0, Math.PI, Math.PI * 2)
          ctx.stroke()
          ctx.beginPath()
          ctx.ellipse(p.x, p.y - R * 0.45, R * 0.8, R * 0.24, 0, 0, Math.PI)
          ctx.stroke()
          // Rim-Licht oben-links
          ctx.strokeStyle = rgba(lighten(p.color, 35), Math.min(1, life * 1.7))
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, R * 0.94, R * 0.86, 0, Math.PI * 1.15, Math.PI * 1.55)
          ctx.stroke()
          break
        }
        case 'streak': {
          // Linie entlang der Bewegung, Schweif hinter dem Kopf
          const tx = p.x - p.vx * 0.085
          const ty = p.y - p.vy * 0.085
          const grad = ctx.createLinearGradient(tx, ty, p.x, p.y)
          grad.addColorStop(0, rgba(p.color, 0))
          grad.addColorStop(1, rgba(p.color, 0.9))
          ctx.strokeStyle = grad
          ctx.lineWidth = p.size
          ctx.lineCap = 'round'
          ctx.beginPath()
          ctx.moveTo(tx, ty)
          ctx.lineTo(p.x, p.y)
          ctx.stroke()
          break
        }
        case 'flash': {
          // Muendungsblitz: 6-Zacken-Stern + heller Kern, additiv
          ctx.save()
          ctx.globalCompositeOperation = 'lighter'
          ctx.translate(p.x, p.y)
          ctx.rotate((p.bornMs % 628) / 100)
          const s = p.size * (0.75 + (1 - life) * 0.5)
          ctx.fillStyle = rgba(p.color, 0.85)
          ctx.beginPath()
          for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2
            const len = s * (i % 2 === 0 ? 1.7 : 0.9)
            const bx = Math.cos(a + Math.PI / 2) * s * 0.16
            const by = Math.sin(a + Math.PI / 2) * s * 0.16
            ctx.moveTo(bx, by)
            ctx.lineTo(Math.cos(a) * len, Math.sin(a) * len)
            ctx.lineTo(-bx, -by)
            ctx.closePath()
          }
          ctx.fill()
          const core = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 0.6)
          core.addColorStop(0, rgba(lighten(p.color, 40), 0.95))
          core.addColorStop(1, rgba(p.color, 0))
          ctx.fillStyle = core
          ctx.beginPath()
          ctx.arc(0, 0, s * 0.6, 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
          break
        }
        case 'groundRing': {
          const t = 1 - life
          const ease = 1 - (1 - t) ** 2
          const R = p.size * (0.3 + 0.7 * ease)
          ctx.strokeStyle = p.color
          ctx.lineWidth = Math.max(1.3, 3.6 * life)
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, R, R * 0.32, 0, 0, Math.PI * 2)
          ctx.stroke()
          break
        }
        case 'groundGlow': {
          const t = 1 - life
          const R = p.size * (0.75 + 0.25 * t)
          ctx.globalAlpha = Math.sin(Math.min(1, t * 1.1) * Math.PI) * 0.6
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.scale(1, 0.32)
          const g = ctx.createRadialGradient(0, 0, 0, 0, 0, R)
          g.addColorStop(0, rgba(p.color, 0.7))
          g.addColorStop(1, rgba(p.color, 0))
          ctx.fillStyle = g
          ctx.beginPath()
          ctx.arc(0, 0, R, 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
          break
        }
        case 'implode': {
          const t = 1 - life
          const R = Math.max(2, p.size * (1.2 - 0.9 * t))
          ctx.strokeStyle = p.color
          ctx.lineWidth = 2.5 + t * 2.5
          ctx.beginPath()
          ctx.arc(p.x, p.y, R, 0, Math.PI * 2)
          ctx.stroke()
          break
        }
        case 'pulse': {
          const t = 1 - life
          const R = p.size * (0.5 + 0.6 * t)
          const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, R)
          g.addColorStop(0, rgba(p.color, 0.75))
          g.addColorStop(1, rgba(p.color, 0))
          ctx.fillStyle = g
          ctx.beginPath()
          ctx.arc(p.x, p.y, R, 0, Math.PI * 2)
          ctx.fill()
          break
        }
        default: {
          ctx.fillStyle = p.color
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }
    ctx.globalAlpha = 1
  }
}
